<template>
  <div class="wallet-detail">
    <!-- HEADER -->
    <div class="wallet-detail-header">
      <div class="wallet-detail-title">
        <v-icon left>mdi-wallet</v-icon>
        <span v-if="account">{{ account.reference || account.id }}</span>
        <span v-else>Wallet Account</span>
      </div>
      <v-spacer />
      <v-btn
        icon
        data-test-id="walletDetailCloseBtn"
        title="Close"
        @click="$emit('close')"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <v-progress-linear v-if="runningAction" indeterminate color="primary" />

    <!-- TABS -->
    <v-tabs v-model="activeTab" class="wallet-tabs">
      <v-tab data-test-id="walletSummaryTab">Summary</v-tab>
      <v-tab data-test-id="walletTransactionsTab">Transactions</v-tab>
      <v-tab data-test-id="walletReservationsTab">Reservations</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" class="wallet-tabs-items">
      <!-- TAB 1: SUMMARY -->
      <v-tab-item>
        <div class="wallet-tab-content" v-if="account">
          <v-simple-table dense class="wallet-summary-table">
            <tbody>
              <tr>
                <td class="font-weight-bold">ID</td>
                <td>{{ account.id }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Reference</td>
                <td>{{ account.reference }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Currency</td>
                <td>{{ account.currency }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Balance</td>
                <td>{{ account.balance }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Credit Limit</td>
                <td>{{ account.creditLimit }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Status</td>
                <td>
                  <v-chip small :color="statusColor(account.status)" dark>
                    {{ account.status }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="wallet-summary-actions mt-4">
            <v-btn
              color="primary"
              outlined
              data-test-id="changeCreditLimitBtn"
              @click="changeCreditLimitDialog = true"
            >
              <v-icon left>mdi-pencil</v-icon>
              Change Credit Limit
            </v-btn>
          </div>
        </div>
      </v-tab-item>

      <!-- TAB 2: TRANSACTIONS -->
      <v-tab-item>
        <div class="wallet-tab-content">
          <div class="wallet-tab-toolbar">
            <v-btn
              small
              :disabled="transactionOffset === 0 || runningTransactions"
              @click="prevTransactionPage"
              data-test-id="prevTransactionPageBtn"
            >
              <v-icon>mdi-chevron-left</v-icon>
              Prev
            </v-btn>
            <span class="mx-2">Offset: {{ transactionOffset }}</span>
            <v-btn
              small
              :disabled="transactions.length < transactionLimit || runningTransactions"
              @click="nextTransactionPage"
              data-test-id="nextTransactionPageBtn"
            >
              Next
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <v-spacer />
            <span class="grey--text">{{ transactions.length }} results</span>
          </div>
          <v-data-table
            dense
            disable-pagination
            hide-default-footer
            fixed-header
            single-select
            :items="transactions"
            :headers="transactionHeaders"
            :loading="runningTransactions"
            no-data-text="No transactions found"
            class="wallet-inner-table"
            data-test-id="transactionsTable"
          >
            <template v-slot:item="{ item }">
              <tr
                :data-test-id="'transaction_' + item.id"
                @click="openTransaction(item)"
                style="cursor: pointer"
              >
                <td>{{ item.id }}</td>
                <td>{{ item.type }}</td>
                <td>{{ item.amount }}</td>
                <td>{{ item.reference }}</td>
                <td>{{ item.description }}</td>
                <td>{{ item.createdAt }}</td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-tab-item>

      <!-- TAB 3: RESERVATIONS -->
      <v-tab-item>
        <div class="wallet-tab-content">
          <div class="wallet-tab-toolbar">
            <v-btn
              small
              :disabled="reservationOffset === 0 || runningReservations"
              @click="prevReservationPage"
              data-test-id="prevReservationPageBtn"
            >
              <v-icon>mdi-chevron-left</v-icon>
              Prev
            </v-btn>
            <span class="mx-2">Offset: {{ reservationOffset }}</span>
            <v-btn
              small
              :disabled="reservations.length < reservationLimit || runningReservations"
              @click="nextReservationPage"
              data-test-id="nextReservationPageBtn"
            >
              Next
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <v-spacer />
            <span class="grey--text">{{ reservations.length }} results</span>
          </div>
          <v-data-table
            dense
            disable-pagination
            hide-default-footer
            fixed-header
            single-select
            :items="reservations"
            :headers="reservationHeaders"
            :loading="runningReservations"
            no-data-text="No reservations found"
            class="wallet-inner-table"
            data-test-id="reservationsTable"
          >
            <template v-slot:item="{ item }">
              <tr
                :data-test-id="'reservation_' + item.id"
                @click="openReservation(item)"
                style="cursor: pointer"
              >
                <td>{{ item.id }}</td>
                <td>{{ item.amount }}</td>
                <td>{{ item.reason }}</td>
                <td>{{ item.reference }}</td>
                <td>
                  <v-chip v-if="item.status" small>{{ item.status }}</v-chip>
                </td>
                <td>{{ item.expiresAt }}</td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-tab-item>
    </v-tabs-items>

    <!-- CHANGE CREDIT LIMIT DIALOG -->
    <v-dialog v-model="changeCreditLimitDialog" max-width="480">
      <v-card>
        <v-card-title>Change Credit Limit</v-card-title>
        <v-card-text>
          <v-form ref="creditLimitForm" @submit.prevent="submitCreditLimit">
            <v-text-field
              v-model="newCreditLimit"
              type="number"
              dense
              outlined
              label="New Credit Limit*"
              min="0"
              :rules="[v => v !== '' && v !== null && v !== undefined || 'Credit limit is required', v => parseInt(v) >= 0 || 'Must be 0 or greater']"
              data-test-id="newCreditLimitInput"
            />
            <v-text-field
              v-model="creditLimitActor"
              dense
              outlined
              label="Actor*"
              :rules="[v => !!v || 'Actor is required']"
              data-test-id="creditLimitActorInput"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="changeCreditLimitDialog = false"
            data-test-id="cancelCreditLimitBtn"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="runningCreditLimit"
            data-test-id="submitCreditLimitBtn"
            @click="submitCreditLimit"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- TRANSACTION DETAIL DIALOG -->
    <WalletTransactionDetail
      v-if="selectedTransaction"
      :transaction-id="selectedTransaction.id"
      :domain="selectedDomain"
      @close="selectedTransaction = null"
    />

    <!-- RESERVATION DETAIL DIALOG -->
    <WalletReservationDetail
      v-if="selectedReservation"
      :reservation-id="selectedReservation.id"
      :domain="selectedDomain"
      @close="selectedReservation = null"
    />
  </div>
</template>

<script>
import WalletTransactionDetail from "./WalletTransactionDetail.vue";
import WalletReservationDetail from "./WalletReservationDetail.vue";

export default {
  name: "WalletDetail",

  components: {
    WalletTransactionDetail,
    WalletReservationDetail,
  },

  props: {
    accountId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      account: null,
      runningAction: false,

      activeTab: 0,
      transactionsLoaded: false,
      reservationsLoaded: false,

      transactions: [],
      transactionLimit: 100,
      transactionOffset: 0,
      runningTransactions: false,

      reservations: [],
      reservationLimit: 100,
      reservationOffset: 0,
      runningReservations: false,

      selectedTransaction: null,
      selectedReservation: null,

      changeCreditLimitDialog: false,
      newCreditLimit: null,
      creditLimitActor: "",
      runningCreditLimit: false,
    };
  },

  async created() {
    await this.loadAccount();
  },

  watch: {
    activeTab(tab) {
      if (tab === 1 && !this.transactionsLoaded) {
        this.loadTransactions();
      } else if (tab === 2 && !this.reservationsLoaded) {
        this.loadReservations();
      }
    },

    changeCreditLimitDialog(open) {
      if (!open) {
        this.newCreditLimit = null;
        this.creditLimitActor = "";
      }
    },
  },

  methods: {
    async loadAccount() {
      this.runningAction = true;
      try {
        const account = await this.$store.$coreApi.coreWalletApi.getAccount(
          this.selectedDomain,
          this.accountId
        );
        if (!account) return;
        this.account = account;
      } finally {
        this.runningAction = false;
      }
    },

    async loadTransactions() {
      this.runningTransactions = true;
      try {
        const data = await this.$store.$coreApi.coreWalletApi.getTransactions(
          this.selectedDomain,
          this.accountId,
          this.transactionLimit,
          this.transactionOffset
        );
        if (!data) return;
        this.transactions = Array.isArray(data) ? data : data.result ?? data.transactions ?? [];
        this.transactionsLoaded = true;
      } finally {
        this.runningTransactions = false;
      }
    },

    async loadReservations() {
      this.runningReservations = true;
      try {
        const data = await this.$store.$coreApi.coreWalletApi.getReservations(
          this.selectedDomain,
          this.accountId,
          this.reservationLimit,
          this.reservationOffset
        );
        if (!data) return;
        this.reservations = Array.isArray(data) ? data : data.result ?? data.reservations ?? [];
        this.reservationsLoaded = true;
      } finally {
        this.runningReservations = false;
      }
    },

    async prevTransactionPage() {
      this.transactionOffset = Math.max(0, this.transactionOffset - this.transactionLimit);
      await this.loadTransactions();
    },

    async nextTransactionPage() {
      this.transactionOffset += this.transactionLimit;
      await this.loadTransactions();
    },

    async prevReservationPage() {
      this.reservationOffset = Math.max(0, this.reservationOffset - this.reservationLimit);
      await this.loadReservations();
    },

    async nextReservationPage() {
      this.reservationOffset += this.reservationLimit;
      await this.loadReservations();
    },

    openTransaction(item) {
      this.selectedTransaction = item;
    },

    openReservation(item) {
      this.selectedReservation = item;
    },

    async submitCreditLimit() {
      const form = this.$refs.creditLimitForm;
      if (!form.validate()) return;

      this.runningCreditLimit = true;
      try {
        const res = await this.$store.$coreApi.coreWalletApi.updateCreditLimit(
          this.selectedDomain,
          this.accountId,
          {
            creditLimit: parseInt(this.newCreditLimit),
            actor: this.creditLimitActor,
          }
        );
        if (!res?.ok) return;
        this.changeCreditLimitDialog = false;
        await this.loadAccount();
      } finally {
        this.runningCreditLimit = false;
      }
    },

    statusColor(status) {
      switch (status) {
        case "ACTIVE":
          return "green";
        case "SUSPENDED":
          return "orange";
        case "CLOSED":
          return "grey";
        default:
          return "grey";
      }
    },
  },

  computed: {
    selectedDomain() {
      return this.$store.state.selectedDomain;
    },

    transactionHeaders() {
      return [
        { text: "ID", value: "id" },
        { text: "Type", value: "type" },
        { text: "Amount", value: "amount" },
        { text: "Reference", value: "reference" },
        { text: "Description", value: "description" },
        { text: "Created At", value: "createdAt" },
      ];
    },

    reservationHeaders() {
      return [
        { text: "ID", value: "id" },
        { text: "Amount", value: "amount" },
        { text: "Reason", value: "reason" },
        { text: "Reference", value: "reference" },
        { text: "Status", value: "status" },
        { text: "Expires At", value: "expiresAt" },
      ];
    },
  },
};
</script>

<style scoped>
.wallet-detail {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: white;
}

.wallet-detail-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  min-height: 56px;
}

.wallet-detail-title {
  display: flex;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 500;
}

.wallet-tabs {
  flex-shrink: 0;
}

.wallet-tabs-items {
  flex: 1 1 auto;
  overflow-y: auto;
}

.wallet-tab-content {
  padding: 16px;
  height: 100%;
}

.wallet-summary-table {
  max-width: 600px;
}

.wallet-tab-toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.wallet-inner-table.v-data-table::v-deep
  > .v-data-table__wrapper
  > table
  > tbody
  > tr {
  cursor: pointer;
}
</style>
